import {
  MENU_POPOVER_FRAME_CLASS_NAME,
  renderMenuPopoverContent,
} from "@-/dev.improve.haniverse.components.ui.menu-popover-surface";
import type { StyleProp, ViewStyle } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { NativeOnlyAnimatedView } from "@-/dev.improve.haniverse.components.ui.native-only-animated-view";
import {
  PortalContextBridgeContent,
  usePortalContextValues,
} from "@-/dev.improve.haniverse.components.ui.portal-context-bridge";
import { TextClassContext, Text } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import {
  CheckboxItem,
  Content,
  Item,
  ItemIndicator,
  Label,
  Overlay,
  Portal,
  RadioItem,
  Separator,
  SubContent,
  SubTrigger,
  useRootContext,
  useSubContext,
  type CheckboxItemProps,
  type CheckboxItemRef,
  type ContentProps,
  type ContentRef,
  type ItemProps,
  type ItemRef,
  type LabelProps,
  type LabelRef,
  type RadioItemProps,
  type RadioItemRef,
  type SeparatorProps,
  type SeparatorRef,
  type SubContentProps,
  type SubContentRef,
  type SubTriggerProps,
  type SubTriggerRef,
} from "@rn-primitives/dropdown-menu";
import { type ComponentProps, type ReactNode, type RefAttributes, use } from "react";
import { Platform, StyleSheet, View, useWindowDimensions } from "@-/com.npmjs.react-native";
import { FadeIn } from "@-/com.npmjs.react-native-reanimated";
import { SafeAreaInsetsContext } from "@-/com.npmjs.react-native-safe-area-context";

export function DropdownMenuSubTrigger({
  children,
  className,
  iconClassName,
  inset,
  ...props
}: SubTriggerProps &
  RefAttributes<SubTriggerRef> & {
    children?: ReactNode;
    iconClassName?: string;
    inset?: boolean;
  }) {
  const { open } = useSubContext();
  let iconName: "LuChevronDown" | "LuChevronRight" | "LuChevronUp" = "LuChevronDown";
  if (Platform.OS === "web") {
    iconName = "LuChevronRight";
  } else if (open) {
    iconName = "LuChevronUp";
  }
  return (
    <TextClassContext
      value={cn(
        "text-sm select-none group-active:text-accent-foreground",
        open && "text-accent-foreground",
      )}
    >
      <SubTrigger
        className={cn(
          "group flex flex-row items-center rounded-xs p-2 active:bg-accent sm:py-1.5",
          Platform.select({
            web: "focus:bg-accent focus:text-accent-foreground cursor-default outline-none [&_svg]:pointer-events-none",
          }),
          open && "bg-accent",
          inset && "pl-8",
          className,
        )}
        {...props}
      >
        <>{children}</>
        <Icon className={cn("ml-auto size-4 shrink-0 text-foreground", iconClassName)}
          name={iconName}
          size={16}
        />
      </SubTrigger>
    </TextClassContext>
  );
}

export function DropdownMenuSubContent({
  className,
  ...props
}: SubContentProps & RefAttributes<SubContentRef>) {
  return (
    <NativeOnlyAnimatedView entering={FadeIn}>
      <SubContent
        className={cn(
          MENU_POPOVER_FRAME_CLASS_NAME,
          "p-1",
          Platform.select({
            web: "animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 fade-in-0 data-[state=closed]:zoom-out-95 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-context-menu-content-transform-origin) z-50 min-w-[8rem]",
          }),
          className,
        )}
        {...props}
      >
        {renderMenuPopoverContent(props.children)}
      </SubContent>
    </NativeOnlyAnimatedView>
  );
}

export function DropdownMenuContent({
  className,
  overlayClassName,
  overlayStyle,
  portalHost,
  style,
  ...props
}: ContentProps &
  RefAttributes<ContentRef> & {
    overlayClassName?: string;
    overlayStyle?: StyleProp<ViewStyle>;
    portalHost?: string;
  }) {
  const contextValues = usePortalContextValues();
  // `triggerPosition` is the `.measure()`d trigger rect (`pageY` + `height`) captured when the menu
  // opened. On native we position the menu ourselves off it (see the block comment below), so the
  // menu drops *below* the trigger instead of sitting at a fixed offset that overlaps the header.
  const { contentLayout, triggerPosition } = useRootContext();

  const flattenedOverlayStyle =
    overlayStyle !== undefined && overlayStyle !== null
      ? StyleSheet.flatten(overlayStyle)
      : undefined;

  let overlayResolvedStyle: ViewStyle | undefined;
  if (Platform.OS === "web") {
    overlayResolvedStyle = flattenedOverlayStyle;
  } else {
    overlayResolvedStyle =
      flattenedOverlayStyle !== undefined
        ? StyleSheet.flatten([StyleSheet.absoluteFill, flattenedOverlayStyle])
        : StyleSheet.absoluteFill;
  }

  const sideOffset = props.sideOffset ?? 0;
  const { height: windowHeight } = useWindowDimensions();
  // Null outside a SafeAreaProvider (component tests): no inset to keep clear of then.
  const safeAreaInsets = use(SafeAreaInsetsContext);
  // Anchor the menu to the measured trigger rather than a fixed top: below the trigger for the
  // default `side="bottom"`, above it for `side="top"` after subtracting the measured content
  // height. Until the trigger is measured, fall back to a stable top so the first frame isn't stuck
  // off-screen (the measure resolves on the next frame).
  let nativeTop = 80;
  if (triggerPosition !== null && triggerPosition !== undefined) {
    const contentHeight = contentLayout?.height ?? 0;
    const above = triggerPosition.pageY - contentHeight - sideOffset;
    const below = triggerPosition.pageY + triggerPosition.height + sideOffset;
    // Like Radix's `avoidCollisions` on web: once the menu has measured itself, open it on the
    // other side when it would cross the safe area on the requested one. A `side="top"` menu on a
    // trigger near the top of the screen (Browse's card actions on Android) otherwise opened above
    // the status bar, and its first items, Favorite among them, could not be reached.
    const fitsAbove = above >= (safeAreaInsets?.top ?? 0);
    const fitsBelow = below + contentHeight <= windowHeight - (safeAreaInsets?.bottom ?? 0);
    if (props.side === "top") {
      nativeTop = contentLayout === null || fitsAbove || !fitsBelow ? above : below;
    } else {
      nativeTop = contentLayout === null || fitsBelow || !fitsAbove ? below : above;
    }
  }

  return (
    <Portal hostName={portalHost}>
      {/*
       * No `FullWindowOverlay` wrapper on native: react-native-screens' `FullWindowOverlay` renders
       * its children in a separate native UIWindow whose `onLayout` never fires back to JS under the
       * New Architecture (Fabric). `@rn-primitives`' dropdown positions the menu only once BOTH the
       * trigger is measured AND the content reports its own layout (`useRelativePosition` returns an
       * off-screen `opacity: 0, top: <screenHeight>` placeholder until then). Inside `FullWindowOverlay`
       * the content's `onLayout` never arrives, so it stays stuck at that off-screen placeholder and the
       * menu silently never appears. We therefore keep `disablePositioningStyle` on native and position
       * the menu ourselves off the measured `triggerPosition`: a full-width inset (`left/right: 16`)
       * dropped just below (or, for `side="top"`, above) the trigger, so it never overlaps the header.
       * The portal host (`@rn-primitives/portal`) already sits at the app root above the navigator, so
       * the menu still overlays page content. Anchored positioning remains enabled on web.
       */}
      <PortalContextBridgeContent values={contextValues}>
        <Overlay className={overlayClassName} style={overlayResolvedStyle}>
          <TextClassContext value="text-popover-foreground">
            <Content
              className={cn(
                MENU_POPOVER_FRAME_CLASS_NAME,
                "min-w-32 p-1",
                Platform.select({
                  web: cn(
                    "z-50 max-h-(--radix-context-menu-content-available-height) origin-(--radix-context-menu-content-transform-origin) cursor-default animate-in fade-in-0 zoom-in-95",
                    props.side === "bottom" && "slide-in-from-top-2",
                    props.side === "top" && "slide-in-from-bottom-2",
                  ),
                }),
                className,
              )}
              disablePositioningStyle={Platform.OS !== "web"}
              style={
                Platform.OS === "web"
                  ? style
                  : StyleSheet.flatten([
                      {
                        left: 16,
                        opacity: contentLayout === null && props.side === "top" ? 0 : undefined,
                        position: "absolute",
                        right: 16,
                        top: nativeTop,
                      },
                      style,
                    ])
              }
              {...props}
            >
              {renderMenuPopoverContent(props.children)}
            </Content>
          </TextClassContext>
        </Overlay>
      </PortalContextBridgeContent>
    </Portal>
  );
}

export function DropdownMenuItem({
  className,
  inset,
  variant,
  ...props
}: ItemProps &
  RefAttributes<ItemRef> & {
    className?: string;
    inset?: boolean;
    variant?: "default" | "destructive";
  }) {
  return (
    <TextClassContext
      value={cn(
        "text-sm text-popover-foreground select-none",
        variant === "destructive" && "text-destructive",
      )}
    >
      <Item
        className={cn(
          "group relative flex flex-row items-center gap-2 rounded-xs p-2 active:bg-accent sm:py-1.5",
          Platform.select({
            web: cn(
              "cursor-default outline-none focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none",
              variant === "destructive" && "focus:bg-destructive/10 dark:focus:bg-destructive/20",
            ),
          }),
          variant === "destructive" && "active:bg-destructive/10 dark:active:bg-destructive/20",
          props.disabled && "opacity-50",
          inset && "pl-8",
          className,
        )}
        {...props}
      />
    </TextClassContext>
  );
}

export function DropdownMenuCheckboxItem({
  children,
  className,
  ...props
}: CheckboxItemProps &
  RefAttributes<CheckboxItemRef> & {
    children?: ReactNode;
  }) {
  return (
    <TextClassContext value="text-sm text-popover-foreground select-none group-active:text-accent-foreground">
      <CheckboxItem
        className={cn(
          "group relative flex flex-row items-center gap-2 rounded-xs py-2 pr-2 pl-8 active:bg-accent sm:py-1.5",
          Platform.select({
            web: "focus:bg-accent focus:text-accent-foreground cursor-default outline-none data-[disabled]:pointer-events-none",
          }),
          props.disabled && "opacity-50",
          className,
        )}
        {...props}
      >
        <View className="absolute left-2 flex size-3.5 items-center justify-center">
          <ItemIndicator>
            <Icon className={cn(
                "size-4 text-foreground",
                Platform.select({ web: "pointer-events-none" }),
              )}
              name="LuCheck"
              size={16}
            />
          </ItemIndicator>
        </View>
        <>{children}</>
      </CheckboxItem>
    </TextClassContext>
  );
}

export function DropdownMenuRadioItem({
  children,
  className,
  ...props
}: RadioItemProps &
  RefAttributes<RadioItemRef> & {
    children?: ReactNode;
  }) {
  return (
    <TextClassContext value="text-sm text-popover-foreground select-none group-active:text-accent-foreground">
      <RadioItem
        className={cn(
          "group relative flex flex-row items-center gap-2 rounded-xs py-2 pr-2 pl-8 active:bg-accent sm:py-1.5",
          Platform.select({
            web: "focus:bg-accent focus:text-accent-foreground cursor-default outline-none data-[disabled]:pointer-events-none",
          }),
          props.disabled && "opacity-50",
          className,
        )}
        {...props}
      >
        <View className="absolute left-2 flex size-3.5 items-center justify-center">
          <ItemIndicator>
            <View className="size-2 rounded-full bg-foreground" />
          </ItemIndicator>
        </View>
        <>{children}</>
      </RadioItem>
    </TextClassContext>
  );
}

export function DropdownMenuLabel({
  className,
  inset,
  ...props
}: LabelProps &
  RefAttributes<LabelRef> & {
    className?: string;
    inset?: boolean;
  }) {
  return (
    <Label
      className={cn(
        "p-2 font-sans text-sm font-medium text-foreground sm:py-1.5",
        inset && "pl-8",
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...props
}: SeparatorProps & RefAttributes<SeparatorRef>) {
  return <Separator className={cn("-mx-1 my-1 h-px bg-border", className)} {...props} />;
}

export function DropdownMenuShortcut({ className, ...props }: ComponentProps<typeof Text>) {
  return (
    <Text
      className={cn("ml-auto font-sans text-xs tracking-widest text-muted-foreground", className)}
      {...props}
    />
  );
}
